// @ts-nocheck -- TypeScript migration in progress: not yet typed.
// Public read-only share links (issue #100), client side.
//
// A capability URL /share/<shareId>#<secret> boots the app in read-only
// public-share mode for ONE flight. This module owns the mode primitives:
//   * detection + parsing of the /share/<id>#<secret> URL
//   * trading the fragment secret for a short-lived share token (in memory)
//   * a fetch shim that serves the app's normal data endpoints from a single
//     pre-loaded /api/public-shares/<id>/bundle response, so the existing boot
//     and flight-render path run UNCHANGED — the shim is the only seam
//   * proxying the PDF report and re-exchanging silently on token expiry
//
// The boot orchestration (which touches app.ts internals) lives in app.ts and
// calls into these primitives.

const SHARE_PATH_RE = /^\/share\/([^/]+)\/?$/;

let shareToken = null;          // in-memory only — never localStorage
let shareTokenExp = 0;          // epoch seconds
let shareBundle = null;         // the loaded /bundle payload
let shareContext = null;        // { shareId, secret }

export function isPublicShareMode() {
  try {
    return SHARE_PATH_RE.test(window.location.pathname);
  } catch {
    return false;
  }
}

// { shareId, secret } from /share/<id>#<secret>. The secret lives only in the
// URL fragment (never sent in a request path or server log).
export function publicShareContext() {
  if (shareContext) return shareContext;
  const match = SHARE_PATH_RE.exec(window.location.pathname);
  const shareId = match ? decodeURIComponent(match[1]) : "";
  const secret = (window.location.hash || "").replace(/^#/, "").trim();
  shareContext = { shareId, secret };
  return shareContext;
}

function synthJson(body, status = 200) {
  return new Response(JSON.stringify(body), {
    status,
    headers: { "content-type": "application/json" },
  });
}

// Trade the fragment secret for a short-lived share token. Returns
// { ok, error }. Called at boot and again (silently) when a request 401s.
export async function initPublicShareSession() {
  const { shareId, secret } = publicShareContext();
  if (!shareId || !secret) {
    return { ok: false, error: "This share link is incomplete." };
  }
  try {
    const response = await window.__publicShareRealFetch(
      `/api/public-shares/${encodeURIComponent(shareId)}/session`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ secret }),
        cache: "no-store",
      },
    );
    if (!response.ok) {
      return { ok: false, error: "This share link is invalid or has expired." };
    }
    const payload = await response.json();
    shareToken = payload.token ?? null;
    shareTokenExp = Number(payload.expiresAt) || 0;
    return shareToken ? { ok: true } : { ok: false, error: "Could not open this shared flight." };
  } catch {
    return { ok: false, error: "Could not reach the server to open this shared flight." };
  }
}

async function ensureFreshToken() {
  // Re-exchange a little before expiry so an in-flight read doesn't 401.
  const nowSec = Math.floor(Date.now() / 1000);
  if (shareToken && shareTokenExp - nowSec > 30) return;
  await initPublicShareSession();
}

async function fetchFromFacade(path, init = {}) {
  await ensureFreshToken();
  const headers = new Headers(init.headers || {});
  if (shareToken) headers.set("authorization", `Bearer ${shareToken}`);
  let response = await window.__publicShareRealFetch(path, { ...init, headers, cache: "no-store" });
  if (response.status === 401) {
    await initPublicShareSession();
    const retry = new Headers(init.headers || {});
    if (shareToken) retry.set("authorization", `Bearer ${shareToken}`);
    response = await window.__publicShareRealFetch(path, { ...init, headers: retry, cache: "no-store" });
  }
  return response;
}

export async function loadPublicShareBundle() {
  const { shareId } = publicShareContext();
  const response = await fetchFromFacade(`/api/public-shares/${encodeURIComponent(shareId)}/bundle`);
  if (!response.ok) throw new Error(`Shared flight unavailable (HTTP ${response.status}).`);
  shareBundle = await response.json();
  return shareBundle;
}

export function publicShareBundle() {
  return shareBundle;
}

// The app-config the shell reads in share mode. readOnly drives the existing
// demo/read-only UI; publicShare is the new flag app.ts keys share behavior on.
export function publicShareAppConfig() {
  return {
    demoMode: false,
    readOnly: true,
    publicShare: true,
    databaseName: "Shared flight",
    askGraphEnabled: false,
    maintenanceEnabled: false,
    // Shared flight pages are a standalone dashboard with no fleet context, so
    // the New UI navigation shell (fleet sidebar) stays off deterministically.
    newUiEnabled: false,
    localMailUiUrl: null,
    auth: { supabaseUrl: "", supabaseAnonKey: "", providers: { email: false, google: false, apple: false } },
  };
}

// Install a window.fetch shim that answers the app's normal data endpoints from
// the pre-loaded bundle, so loadAppConfig / the flight manifest / the flight
// render all run unchanged. Anything not needed by the read-only view is denied.
export function installPublicShareFetchShim() {
  if (window.__publicShareRealFetch) return; // idempotent
  const realFetch = window.fetch.bind(window);
  window.__publicShareRealFetch = realFetch;
  const { shareId } = publicShareContext();

  window.fetch = async (input, init = {}) => {
    const url = typeof input === "string" ? input : (input?.url ?? String(input));
    let pathname = url;
    try { pathname = new URL(url, window.location.origin).pathname; } catch { /* keep raw */ }

    // Non-API and public-share facade calls go straight through.
    if (!pathname.startsWith("/api/")) return realFetch(input, init);
    if (pathname.startsWith("/api/public-shares/")) return fetchFromFacade(url, init);

    const b = shareBundle;
    const flightId = b?.flight?.id;
    const aircraftId = b?.aircraft?.id;

    if (pathname === "/api/app-config") return synthJson(publicShareAppConfig());
    if (pathname === "/api/health") return synthJson({ ok: true, readOnly: true });
    if (pathname === "/api/version" || pathname === "/api/versions") return realFetch(input, init);

    // Aircraft list / summary → just the shared aircraft.
    if (pathname === "/api/aircraft") return synthJson({ aircraft: b?.aircraft ? [b.aircraft] : [] });
    // Flights list → just the shared flight. Ensure the flight carries the
    // cached AI-summary id so the client's flightAiSummaryAvailable() predicate
    // (which keys on current_ai_summary_id) auto-loads it — the pointer can be
    // null on the row even when a summary exists.
    if (pathname === "/api/flights") {
      const flight = b?.flight
        ? { ...b.flight, current_ai_summary_id: b.flight.current_ai_summary_id ?? b.aiSummary?.id ?? null }
        : null;
      return synthJson({ flights: flight ? [flight] : [] });
    }

    // Per-flight reads served from the bundle (read-existing-only).
    if (flightId && pathname === `/api/flights/${flightId}/normalized-artifact`) {
      return b?.normalizedArtifact
        ? synthJson({ artifact: b.normalizedArtifact })
        : synthJson({ error: "Not found." }, 404);
    }
    if (flightId && pathname === `/api/flights/${flightId}/analysis-run`) {
      return b?.analysisRun
        ? synthJson({ analysisRun: b.analysisRun })
        : synthJson({ error: "Not found." }, 404);
    }
    if (flightId && pathname === `/api/flights/${flightId}/ai-summary`) {
      // Mirror the real GET's fresh-cache shape ({ aiSummary, created, stale });
      // the share bundle only ever carries an existing (cached) summary.
      return b?.aiSummary
        ? synthJson({ aiSummary: b.aiSummary, created: false, stale: false })
        : synthJson({ error: "AI summary not found." }, 404);
    }
    if (flightId && pathname === `/api/flights/${flightId}/report`) {
      // Stream the real PDF through the facade (bearer attached there).
      const suffix = url.includes("?") ? url.slice(url.indexOf("?")) : "";
      return fetchFromFacade(`/api/public-shares/${encodeURIComponent(shareId)}/report${suffix}`, init);
    }
    // Saved special-test results (GAMI sweeps) — served read-only from the
    // bundle. Handles both the list (/special-tests) and per-kind
    // (/special-tests/<kind>, e.g. gami-sweep) GETs the app makes; any write is
    // refused. Shape mirrors the real endpoint ({ specialTestResults }).
    const specialTestsBase = flightId ? `/api/flights/${flightId}/special-tests` : "";
    if (specialTestsBase && (pathname === specialTestsBase || pathname.startsWith(`${specialTestsBase}/`))) {
      const method = (init.method || "GET").toUpperCase();
      if (method !== "GET" && method !== "HEAD") return synthJson({ error: "This shared flight is read-only." }, 403);
      const all = Array.isArray(b?.specialTestResults) ? b.specialTestResults : [];
      if (pathname.length > specialTestsBase.length) {
        const kind = decodeURIComponent(pathname.slice(specialTestsBase.length + 1)).split("/")[0].toLowerCase();
        return synthJson({ specialTestResults: all.filter((r) => String(r?.test_kind ?? "").toLowerCase() === kind) });
      }
      return synthJson({ specialTestResults: all });
    }
    // Read-only view never needs these; answer empty so callers don't error.
    if (flightId && pathname === `/api/flights/${flightId}/review-notes`) return synthJson({ notes: [] });
    if (flightId && pathname === `/api/flights/${flightId}/route`) return synthJson({ flight: b?.flight ?? null });

    // Anything else under /api is not part of the read-only share surface.
    return synthJson({ error: "Not available for a shared flight." }, 403);
  };
}
